import React from "react";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { buildWhatsAppLink, IMAGES } from "../constants";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-24 overflow-hidden"
    >
      {/* Decorative background blots */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-cream blur-3xl opacity-60" />
      <div className="pointer-events-none absolute top-40 -right-24 w-[420px] h-[420px] rounded-full bg-sand blur-3xl opacity-80" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Text */}
        <div className="lg:col-span-6 z-10">
          <div className="fade-up flex items-center gap-2 mb-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-vino/30 bg-page/60 backdrop-blur-sm px-4 py-1.5 text-[11px] uppercase tracking-[0.3em] text-vino font-medium">
              <Sparkles size={12} /> Fé, Tradición y Amor
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="fade-up delay-1 font-heading text-[44px] leading-[0.95] sm:text-6xl lg:text-7xl xl:text-[88px] tracking-tighter text-ink"
          >
            Vestimos <span className="italic text-vino">historia</span>,
            <br className="hidden sm:block" />
            unimos <span className="italic">familias</span>.
          </h1>

          <p className="fade-up delay-2 mt-6 sm:mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-ink-soft font-body">
            Ropa personalizada con fe, estilo y propósito. Nombres, logos,
            equipos y devociones.
          </p>

          <div className="fade-up delay-3 mt-8 sm:mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={buildWhatsAppLink("Hola Vallitas, quiero personalizar mi prenda.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-vino text-page px-7 py-4 text-[15px] font-medium hover:bg-vino-deep transition-all hover:-translate-y-1 shadow-[0_18px_40px_-15px_rgba(114,47,55,0.55)]"
            >
              Personaliza la tuya
              <ArrowUpRight
                size={18}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
            <a
              href="#productos"
              data-testid="hero-explore-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent border border-vino/40 text-ink px-7 py-4 text-[15px] font-medium hover:bg-sand transition-colors"
            >
              Ver colección
            </a>
          </div>

          {/* Mini stats / trust */}
          <div className="fade-up delay-4 mt-12 grid grid-cols-3 gap-4 max-w-md">
            <div data-testid="stat-clients">
              <div className="font-heading text-3xl text-vino">+800</div>
              <div className="text-xs uppercase tracking-[0.18em] text-ink-soft mt-1">
                Familias vestidas
              </div>
            </div>
            <div data-testid="stat-teams">
              <div className="font-heading text-3xl text-vino">+45</div>
              <div className="text-xs uppercase tracking-[0.18em] text-ink-soft mt-1">
                Equipos & marcas
              </div>
            </div>
            <div data-testid="stat-from">
              <div className="font-heading text-3xl text-vino">$10</div>
              <div className="text-xs uppercase tracking-[0.18em] text-ink-soft mt-1">
                Desde
              </div>
            </div>
          </div>
        </div>

        {/* Visual */}
        <div className="lg:col-span-6 relative">
          <div className="fade-up delay-2 relative">
            <div className="img-zoom rounded-[2.5rem] overflow-hidden aspect-[4/5] shadow-[0_50px_120px_-30px_rgba(44,36,36,0.45)]">
              <img
                src={IMAGES.hero}
                alt="Naiderly Marquez, fundadora de Vallitas"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
            </div>

            {/* Floating tag */}
            <div className="absolute -left-4 sm:-left-10 bottom-10 paper-card rounded-2xl px-5 py-4 max-w-[220px] float-anim">
              <div className="text-[10px] uppercase tracking-[0.25em] mb-1">
                CEO
              </div>
              <div className="font-heading text-xl leading-tight">
                Naiderly Marquez
              </div>
            </div>

            {/* Floating quality badge */}
            <div className="hidden sm:flex absolute -right-4 top-10 paper-card rounded-2xl px-5 py-4 items-center gap-3 float-anim" style={{ animationDelay: "1s" }}>
              <div className="w-10 h-10 rounded-full bg-vino text-page flex items-center justify-center font-heading text-lg">
                ✦
              </div>
              <div>
                <div className="text-xs text-ink-soft">Confección</div>
                <div className="font-medium text-sm text-ink">artesanal</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="mt-16 sm:mt-24 border-y border-line bg-sand overflow-hidden">
        <div className="flex marquee whitespace-nowrap py-4 sm:py-5 gap-12">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex gap-12 items-center pr-12">
              {["Bodys", "Jerseys", "Activewear", "Equipos", "Familias", "Fe", "Identidad", "Bodys", "Jerseys", "Activewear"].map(
                (w, i) => (
                  <span
                    key={`${k}-${i}`}
                    className="font-heading italic text-2xl sm:text-3xl text-vino"
                  >
                    {w} <span className="text-gold mx-4">✦</span>
                  </span>
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
